import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { relaySitesApi, type RelaySite, type RelayAdapterType, type AuthType, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TableShell, type ColumnSpec } from "@/components/TableShell";
import { InheritedModelConfigEditor } from "@/components/InheritedModelConfigEditor";
import { ApiTestConsole } from "@/components/ApiTestConsole";
import { relayAuthHeaderLine, isNewAPIFamily, normalizeModulePath, profileForSite } from "@/adapters/relayProfiles";

// ---------------------------------------------------------------------------
// /relay-sites — 中转站点 CRUD(v6 重构后拆分页;B4.3)。
// 后端 RelaySite(原 Sites.tsx),字段 site_type 固定 relay + adapter_type
// (newapi/sub2api/oneapi);user_id → platform_user_id。
// ---------------------------------------------------------------------------

const emptyForm = {
  code: "",
  name: "",
  url: "",
  site_type: "relay" as const,
  adapter_type: "newapi" as RelayAdapterType,
  auth_type: "token" as AuthType,
  auth: "",
  platform_user_id: 0,
  // module_path = 业务模块路径（落在后端 site_options.module_path）：
  // 决定渠道 API 前缀 /api/{module_path}/channel，空 = /api/channel。
  // 与 url 里的路径无关 —— url 只填 scheme://host，两者分开配置。
  module_path: "",
  enabled: true,
  remark: "",
  models: null as string | null,
  model_mapping: null as string | null,
};

// 表单用扁平 module_path，后端 API 是 site_options.module_path（与 push_site 同构）。
// 空串照传：后端把空值规整为「未配置」，并用 profile 默认值兜底变体站点。
const toSiteOptions = (modulePath: string) => ({
  site_options: { module_path: modulePath.trim() },
});

// 变体站点未填业务模块路径时的默认值。切到该适配器时自动预填，
// 运营只在「同质站点」时改（如 zzz/supplier）。
const defaultModulePath = (adapterType: string) => (adapterType === "newapi-seller" ? "seller" : "");

// doc/008: 探针指纹 —— 被测对象是「适配器+URL+认证方式+凭证+用户ID」组合,
// 任一变化即视为被测对象已变,之前通过的结果自动失效(doc/008 §2.2,指纹工具在 lib/probe.ts)。
import { probeKey, probeTime } from "@/lib/probe";

export function RelaySitesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["relay-sites"], queryFn: relaySitesApi.list });
  const [editing, setEditing] = useState<RelaySite | null>(null);
  const [testing, setTesting] = useState<RelaySite | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [creatingTest, setCreatingTest] = useState(false);
  const [createdTestedKey, setCreatedTestedKey] = useState<string | null>(null);

  // 指纹必须含 module_path：换业务模块路径 = 换了被测地址，旧的「通过」不再
  // 成立（否则运营改完路径不重测就能保存）。
  const createKey = probeKey(form.adapter_type, form.url, normalizeModulePath(form.module_path), form.auth_type, form.auth, form.platform_user_id);
  const createTested = createdTestedKey === createKey;

  const handleCreateTest = async () => {
    setCreatingTest(true);
    setMsg(null);
    try {
      const r = await relaySitesApi.testCredentials({
        adapter_type: form.adapter_type,
        url: form.url,
        auth_type: form.auth_type,
        auth: form.auth,
        ...toSiteOptions(form.module_path),
        platform_user_id: form.platform_user_id,
      });
      if (r.ok) {
        setCreatedTestedKey(createKey);
        setMsg(`新凭证测试通过(${probeTime(r.tested_at)})`);
      } else {
        setCreatedTestedKey(null);
        setMsg(`测试失败: ${r.error ?? "未知"}`);
      }
    } catch (e) {
      setCreatedTestedKey(null);
      setMsg(e instanceof ApiError ? e.message : "测试失败");
    } finally {
      setCreatingTest(false);
    }
  };

  const createMut = useMutation({
    mutationFn: () => relaySitesApi.create({ ...form, ...toSiteOptions(form.module_path) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relay-sites"] });
      setCreating(false);
      setForm(emptyForm);
      setCreatedTestedKey(null);
      setMsg("创建成功");
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "创建失败"),
  });

  // 控制台需要已落库的站点 id + 已保存凭证,故「打开控制台」= 先保存、再打开。
  const handleCreateAndOpenConsole = async () => {
    if (!createTested) return;
    try {
      const site = await createMut.mutateAsync();
      setTesting(site);
    } catch {
      // createMut.onError 已展示错误信息
    }
  };

  const deleteMut = useMutation({
    mutationFn: (id: number) => relaySitesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relay-sites"] });
      setMsg("删除成功");
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "删除失败"),
  });

  const testMut = useMutation({
    mutationFn: (id: number) => relaySitesApi.test(id),
    onSuccess: (r) => {
      const at = r.tested_at ? new Date(r.tested_at).toLocaleTimeString() : "—";
      setMsg(r.ok ? `连通性正常(${at})` : `失败(${at}): ${r.error ?? "未知"}`);
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "测试失败"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof relaySitesApi.update>[1] }) =>
      relaySitesApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relay-sites"] });
      setEditing(null);
      setNameError(null);
      setMsg("已保存");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setNameError(e.message);
      } else {
        setNameError(null);
        setMsg(e instanceof ApiError ? e.message : "保存失败");
      }
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">中转站点</h1>
        <Button onClick={() => { setCreating(true); setEditing(null); setMsg(null); setCreatedTestedKey(null); }}>+ 新建</Button>
      </div>

      {msg && <div className="text-sm text-muted-foreground">{msg}</div>}

      {creating && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">新建中转站点</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }}
              className="grid grid-cols-2 gap-3"
            >
              <Field label="编码 code" hint="站点稳定身份,表内唯一,仅字母数字,创建后不可改(≤50)。">
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required pattern="[a-zA-Z0-9]+" maxLength={50} placeholder="relay-east-01" />
              </Field>
              <Field label="名称"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
              <Field
                label="URL"
                hint={
                  form.adapter_type === "newapi-seller"
                    ? "只填 scheme://host[:port]。卖家控制台的 /seller 是业务模块，不是部署前缀 —— 填在这里会报错，请填到下面的「业务模块路径」。"
                    : undefined
                }
              >
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" required />
              </Field>
              <Field label="适配器类型">
                <select
                  className="h-10 rounded-md border bg-background px-3 text-sm"
                  value={form.adapter_type}
                  onChange={(e) => {
                    const adapter_type = e.target.value as RelayAdapterType;
                    setForm({
                      ...form,
                      adapter_type,
                      // 切到变体时预填默认业务模块路径；运营已手填的（如 zzz/supplier）不覆盖。
                      module_path:
                        form.module_path === "" || form.module_path === defaultModulePath(form.adapter_type)
                          ? defaultModulePath(adapter_type)
                          : form.module_path,
                    });
                  }}
                >
                  <option value="newapi">newapi</option>
                  <option value="newapi-seller">newapi-seller(卖家控制台)</option>
                  <option value="sub2api">sub2api</option>
                  <option value="oneapi">oneapi(未接入适配器)</option>
                </select>
              </Field>
              {isNewAPIFamily(form.adapter_type) && (
                <Field
                  label="业务模块路径"
                  hint="渠道 API = /api/{此项}/channel。留空 = /api/channel；卖家站填 seller；同质站点填 zzz/supplier。只填 /api 与 /channel 之间那段，别带 api 或 channel。"
                >
                  <Input
                    value={form.module_path}
                    onChange={(e) => setForm({ ...form, module_path: e.target.value })}
                    placeholder={form.adapter_type === "newapi-seller" ? "seller" : "留空 = /api/channel"}
                  />
                </Field>
              )}
              <Field label="认证方式">
                <select className="h-10 rounded-md border bg-background px-3 text-sm" value={form.auth_type} onChange={(e) => setForm({ ...form, auth_type: e.target.value as AuthType })}>
                  <option value="token">token</option>
                  <option value="basic">basic (user:pass)</option>
                </select>
              </Field>
              <Field label="凭证(明文,落库前会加密)" className="col-span-2">
                <Input type="text" value={form.auth} onChange={(e) => setForm({ ...form, auth: e.target.value })} required minLength={1} placeholder="token / user:pass" />
              </Field>
              <Field
                label="对应系统用户Id"
                className="col-span-2"
                hint="下游实例里访问令牌所属用户的数字 ID,通常 admin 为 1;填 0 表示不发送该 header。"
              >
                <Input type="number" min="0" value={form.platform_user_id} onChange={(e) => setForm({ ...form, platform_user_id: Number(e.target.value) })} placeholder="通常填 1" />
              </Field>
              <Field label="备注" className="col-span-2">
                <Input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
              </Field>
              <div className="col-span-2 rounded-lg border bg-muted/20 p-3">
                <InheritedModelConfigEditor
                  inheritable={false}
                  models={form.models}
                  modelMapping={form.model_mapping}
                  onModelsChange={(models) => setForm({ ...form, models })}
                  onModelMappingChange={(model_mapping) => setForm({ ...form, model_mapping })}
                />
              </div>
              <div className="col-span-2 space-y-1">
                {creatingTest && <p className="text-xs text-muted-foreground">正在测试…</p>}
                {!createTested && (
                  <p className="text-xs text-muted-foreground">新凭证需先通过「测试」才能保存。</p>
                )}
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => { setCreating(false); setEditing(null); setForm(emptyForm); setCreatedTestedKey(null); }}>取消</Button>
                  <Button type="button" variant="outline" disabled={creatingTest} onClick={handleCreateTest}>
                    {creatingTest ? "测试中…" : "测试"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!createTested || creatingTest || createMut.isPending}
                    onClick={handleCreateAndOpenConsole}
                    title={createTested ? "保存并打开接口测试控制台" : "请先通过「测试」"}
                  >
                    打开控制台
                  </Button>
                  <Button type="submit" disabled={createMut.isPending || creatingTest || !createTested}>
                    {createMut.isPending ? "提交中…" : "保存"}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {isLoading && <div className="text-sm text-muted-foreground">加载中…</div>}
          {error && <div className="text-sm text-destructive">加载失败: {String(error)}</div>}
          {data && (
            <RelaySitesTable
              sites={data.items}
              onOpen={(s) => { setTesting(s); setEditing(null); }}
              onEdit={(s) => { setEditing(s); setMsg(null); }}
              onTest={(id) => testMut.mutate(id)}
              onDelete={(s) => { if (confirm(`删除 ${s.name}?`)) deleteMut.mutate(s.id); }}
              testing={testMut.isPending}
              deleting={deleteMut.isPending}
            />
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditRelaySiteModal
          s={editing}
          saving={updateMut.isPending}
          saveError={updateMut.error instanceof ApiError ? updateMut.error : null}
          nameError={nameError}
          onNameErrorClear={() => setNameError(null)}
          onClose={() => { if (!updateMut.isPending) { setEditing(null); setNameError(null); setMsg(null); } }}
          onSave={(body) => updateMut.mutate({ id: editing.id, body })}
          onOpenConsole={() => { setTesting(editing); setEditing(null); }}
        />
      )}
      {testing && <RelaySiteTestDrawer site={testing} onClose={() => setTesting(null)} />}
    </div>
  );
}

function RelaySitesTable({
  sites,
  onOpen,
  onEdit,
  onTest,
  onDelete,
  testing,
  deleting,
}: {
  sites: RelaySite[];
  onOpen: (s: RelaySite) => void;
  onEdit: (s: RelaySite) => void;
  onTest: (id: number) => void;
  onDelete: (s: RelaySite) => void;
  testing: boolean;
  deleting: boolean;
}) {
  const [enabledFilter, setEnabledFilter] = useState<"all" | "enabled" | "disabled">("all");
  const rows = useMemo(
    () => sites.filter((s) => enabledFilter === "all" || (enabledFilter === "enabled") === s.enabled),
    [sites, enabledFilter],
  );
  const columns: ColumnSpec<RelaySite>[] = useMemo(
    () => [
      { key: "id", label: "ID", sortable: true },
      { key: "code", label: "编码", sortable: true },
      { key: "name", label: "名称", sortable: true },
      { key: "url", label: "URL" },
      { key: "adapter_type", label: "适配器" },
      { key: "auth_hint", label: "凭证" },
      { key: "target_code", label: "目标代号" },
      { key: "enabled", label: "启用", sortable: true },
      { key: "last_tested_at", label: "最近测试" },
      { key: "actions", label: "", align: "right" },
    ],
    [],
  );
  return (
    <TableShell
      mode="client"
      columns={columns}
      data={rows}
      emptyText={sites.length === 0 ? '暂无站点,点击右上角"+ 新建"开始' : "无匹配站点"}
      search={{
        placeholder: "搜索编码/名称/URL/目标代号",
        match: (s) => `${s.code} ${s.name} ${s.url} ${s.target_code}`,
      }}
      filters={
        <div className="flex items-center gap-1 text-xs">
          {(["all", "enabled", "disabled"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setEnabledFilter(v)}
              className={`rounded-md border px-2 py-1 transition-colors ${
                enabledFilter === v ? "border-primary bg-primary/5 text-primary" : "border-[#e4eaf1] text-muted-foreground hover:bg-muted/40"
              }`}
            >
              {v === "all" ? "全部" : v === "enabled" ? "启用" : "禁用"}
            </button>
          ))}
        </div>
      }
      rowKey={(s) => s.id}
      renderRow={(s) => (
        <tr className="cursor-pointer border-b hover:bg-muted/30" onClick={() => onOpen(s)} title="点击打开接口测试控制台">
          <td className="py-2">{s.id}</td>
          <td className="font-mono text-xs">{s.code}</td>
          <td className="font-medium">{s.name}</td>
          <td className="text-xs text-muted-foreground">{s.url}</td>
          <td><Badge variant="muted">{s.adapter_type}</Badge></td>
          <td className="font-mono text-xs">{s.auth_hint}</td>
          <td className="font-mono text-xs">{s.target_code}</td>
          <td>{s.enabled ? <Badge variant="success">启用</Badge> : <Badge variant="muted">禁用</Badge>}</td>
          <td className="text-xs">
            {s.last_tested_at ? (
              <Badge variant={s.last_test_result === "success" ? "success" : "destructive"}>{s.last_test_result}</Badge>
            ) : <span className="text-muted-foreground">—</span>}
          </td>
          <td className="text-right space-x-1">
            <Button size="sm" variant="outline" onClick={(event) => { event.stopPropagation(); onEdit(s); }} title="编辑站点">
              <Pencil className="mr-1 h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" disabled={testing} onClick={(event) => { event.stopPropagation(); onTest(s.id); }}>测试</Button>
            <Button size="sm" variant="destructive" disabled={deleting} onClick={(event) => { event.stopPropagation(); onDelete(s); }}>删除</Button>
          </td>
        </tr>
      )}
    />
  );
}

function Field({ label, children, className = "", hint }: { label: string; children: React.ReactNode; className?: string; hint?: string }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground leading-relaxed">{hint}</p>}
    </div>
  );
}

function EditRelaySiteModal({
  s,
  saving,
  saveError,
  nameError,
  onNameErrorClear,
  onClose,
  onSave,
  onOpenConsole,
}: {
  s: RelaySite;
  saving: boolean;
  saveError: ApiError | null;
  nameError: string | null;
  onNameErrorClear: () => void;
  onClose: () => void;
  onSave: (body: Parameters<typeof relaySitesApi.update>[1]) => void;
  onOpenConsole: () => void;
}) {
  const [form, setForm] = useState({
    name: s.name,
    url: s.url,
    adapter_type: s.adapter_type,
    auth_type: s.auth_type,
    auth: "",
    platform_user_id: s.platform_user_id,
    module_path: s.site_options?.module_path ?? "",
    enabled: s.enabled,
    remark: s.remark,
    models: s.models,
    model_mapping: s.model_mapping,
  });
  const [err, setErr] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testedKey, setTestedKey] = useState<string | null>(null);
  const [testInfo, setTestInfo] = useState<string | null>(null);

  const curKey = probeKey(form.adapter_type, form.url, normalizeModulePath(form.module_path), form.auth_type, form.auth, form.platform_user_id);
  const authChanged = form.auth !== "";
  const tested = authChanged && testedKey === curKey;

  const handleTest = async () => {
    setTesting(true);
    setErr(null);
    setTestInfo(null);
    try {
      if (authChanged) {
        const r = await relaySitesApi.testCredentials({
          adapter_type: form.adapter_type,
          url: form.url,
          auth_type: form.auth_type,
          auth: form.auth,
          ...toSiteOptions(form.module_path),
          platform_user_id: form.platform_user_id,
        });
        if (r.ok) {
          setTestedKey(curKey);
          setTestInfo(`新凭证测试通过(${probeTime(r.tested_at)})`);
        } else {
          setTestedKey(null);
          setErr(`新凭证测试失败: ${r.error ?? "未知"}`);
        }
      } else {
        const r = await relaySitesApi.test(s.id);
        if (r.ok) {
          setTestInfo(`现有凭证测试通过(${probeTime(r.tested_at)})`);
        } else {
          setErr(`现有凭证测试失败: ${r.error ?? "未知"}`);
        }
      }
    } catch (e) {
      setTestedKey(null);
      setErr(e instanceof ApiError ? e.message : "测试失败");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl border bg-card p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">编辑站点</h2>
          <span className="text-xs text-muted-foreground">原名:{s.name}</span>
        </div>
        {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
        <form
          className="grid grid-cols-2 gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setErr(null);
            onNameErrorClear();
            const body: Parameters<typeof relaySitesApi.update>[1] = {
              name: form.name.trim(),
              url: form.url.trim(),
              site_type: "relay",
              adapter_type: form.adapter_type,
              auth_type: form.auth_type,
              enabled: form.enabled,
              remark: form.remark.trim(),
              platform_user_id: form.platform_user_id,
              models: form.models,
              model_mapping: form.model_mapping,
              ...toSiteOptions(form.module_path),
            };
            if (form.auth !== "") body.auth = form.auth;
            onSave(body);
          }}
        >
          <Field label="名称">
            <Input
              className={nameError ? "border-destructive focus-visible:ring-destructive" : ""}
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); if (nameError) onNameErrorClear(); }}
              required
            />
            {nameError && <p className="text-xs text-destructive">{nameError}</p>}
          </Field>
          <Field
            label="URL"
            hint={
              form.adapter_type === "newapi-seller"
                ? "只填 scheme://host[:port]。卖家控制台的 /seller 是业务模块，不是部署前缀 —— 填在这里会报错，请填到下面的「业务模块路径」。"
                : undefined
            }
          >
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required />
          </Field>
          <Field label="适配器类型">
            <select
              className="h-10 rounded-md border bg-background px-3 text-sm"
              value={form.adapter_type}
              onChange={(e) => {
                const adapter_type = e.target.value as RelayAdapterType;
                setForm({
                  ...form,
                  adapter_type,
                  module_path:
                    form.module_path === "" || form.module_path === defaultModulePath(form.adapter_type)
                      ? defaultModulePath(adapter_type)
                      : form.module_path,
                });
              }}
            >
              <option value="newapi">newapi</option>
              <option value="newapi-seller">newapi-seller(卖家控制台)</option>
              <option value="sub2api">sub2api</option>
              <option value="oneapi">oneapi(未接入适配器)</option>
            </select>
          </Field>
          {isNewAPIFamily(form.adapter_type) && (
            <Field
              label="业务模块路径"
              hint="渠道 API = /api/{此项}/channel。留空 = /api/channel；卖家站填 seller；同质站点填 zzz/supplier。只填 /api 与 /channel 之间那段，别带 api 或 channel。"
            >
              <Input
                value={form.module_path}
                onChange={(e) => setForm({ ...form, module_path: e.target.value })}
                placeholder={form.adapter_type === "newapi-seller" ? "seller" : "留空 = /api/channel"}
              />
            </Field>
          )}
          <Field label="认证方式">
            <select className="h-10 rounded-md border bg-background px-3 text-sm" value={form.auth_type} onChange={(e) => setForm({ ...form, auth_type: e.target.value as AuthType })}>
              <option value="token">token</option>
              <option value="basic">basic (user:pass)</option>
            </select>
          </Field>
          <Field
            label="重置凭证(留空 = 不改)"
            className="col-span-2"
            hint={`当前凭证提示:${s.auth_hint || "—"}。仅当需要替换时填写,留空则保持原 AES 密文不变。`}
          >
            <Input
              type="text"
              value={form.auth}
              onChange={(e) => setForm({ ...form, auth: e.target.value })}
              placeholder="留空保持原值"
            />
          </Field>
          <Field label="启用">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="h-4 w-4 rounded border"
              />
              {form.enabled ? "启用 — 接受新分发" : "禁用 — 不再接受新分发,已存在的渠道保留"}
            </label>
          </Field>
          <Field
            label="对应系统用户Id"
            className="col-span-2"
            hint={`当前值:${s.platform_user_id},通常 admin 为 1;填 0 表示不发送该 header。`}
          >
            <Input type="number" min="0" value={form.platform_user_id} onChange={(e) => setForm({ ...form, platform_user_id: Number(e.target.value) })} />
          </Field>
          <Field label="备注" className="col-span-2">
            <Input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
          </Field>
          <div className="col-span-2 rounded-lg border bg-muted/20 p-3">
            <InheritedModelConfigEditor
              inheritable={false}
              models={form.models}
              modelMapping={form.model_mapping}
              onModelsChange={(models) => setForm({ ...form, models })}
              onModelMappingChange={(model_mapping) => setForm({ ...form, model_mapping })}
            />
          </div>
          <div className="col-span-2 space-y-1 pt-2">
            {authChanged ? (
              tested ? (
                <p className="text-xs text-emerald-600">新凭证已测试通过,可保存</p>
              ) : (
                <p className="text-xs text-muted-foreground">新凭证需先通过「测试」才能保存;留空凭证(=不改)可直接保存。</p>
              )
            ) : (
              testInfo && <p className="text-xs text-emerald-600">{testInfo}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>取消</Button>
              <Button type="button" variant="outline" disabled={testing} onClick={handleTest}>
                {testing ? "测试中…" : "测试"}
              </Button>
              <Button type="button" variant="outline" onClick={onOpenConsole}>打开控制台</Button>
              <Button type="submit" disabled={saving || testing || (authChanged && !tested)}>
                {saving ? "保存中…" : "保存"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function RelaySiteTestDrawer({ site, onClose }: { site: RelaySite; onClose: () => void }) {
  // Q7：new-api 谱系按站点自己的业务模块路径重写端点前缀，因此「卖家站」与
  // 「同质站点」(zzz/supplier) 共用一份 profile —— 控制台里测的地址就是
  // 实际分发用的地址，运营不需要手改路径。
  const modulePath = site.site_options?.module_path ?? "";
  const profile = profileForSite(site.adapter_type, modulePath);
  // 展示用的渠道前缀：站点的 module_path 优先，变体再回落 profile 默认值。
  const effectiveModule = normalizeModulePath(modulePath) || defaultModulePath(site.adapter_type);
  const channelBaseLabel = effectiveModule ? `/api/${effectiveModule}/channel` : "/api/channel";
  return (
    <ApiTestConsole
      title={`接口测试控制台 · ${site.name}`}
      adapterType={site.adapter_type}
      url={site.url}
      supported={!!profile}
      unsupportedNote={<>当前仅 newapi / newapi-seller / sub2api 支持；<code>{site.adapter_type}</code> 尚未接入。</>}
      profile={profile ? { ...profile, baseUrl: site.url } : undefined}
      authHeaderLine={relayAuthHeaderLine(site.adapter_type, site.auth_hint, site.platform_user_id)}
      proxy={(body) => relaySitesApi.proxy(site.id, body)}
      note={
        isNewAPIFamily(site.adapter_type)
          ? `渠道接口前缀 ${channelBaseLabel}（由「业务模块路径」决定，与 URL 无关）`
          : undefined
      }
      onClose={onClose}
    />
  );
}
